import * as React from 'react'
import styled, { createGlobalStyle } from 'styled-components'

import { transition, useTransition } from '../behaviours/transition'
import * as glyphs from '../icons/glyphs/index'
import * as colors from '../constants/colors'
import * as S from '../styles'

const Wrapper = transition.fromBottom(S.modal.Wrapper)

export type DeprecatedModalProps = {
  children: React.ReactNode
  className?: string
  closable?: boolean
  lockScroll?: boolean
  onClose?: () => void
  open: boolean
}

export function DeprecatedModalComponent({
  children,
  className,
  closable = false,
  lockScroll = false,
  onClose,
  open,
}: DeprecatedModalProps): React.ReactElement<DeprecatedModalProps> | null {
  const modalRef = React.useRef<HTMLDivElement>(null)

  function handleOverlayClick(event: React.MouseEvent<HTMLDivElement>): void {
    if (modalRef.current === null) {
      return
    }
    if (closable && onClose && !modalRef.current.contains(event.target as Node)) {
      onClose()
    }
  }

  const { step, unmounted, onTransitionEnd } = useTransition(open)

  if (unmounted) {
    return null
  }

  return (
    <S.modal.Overlay step={step} className={className} onClick={handleOverlayClick}>
      {lockScroll && <DisableBodyScroll />}
      <Wrapper step={step} onTransitionEnd={onTransitionEnd} ref={modalRef}>
        {closable && <glyphs.stroke.Close size={14} onClick={onClose} />}
        <S.modal.Body>{children}</S.modal.Body>
      </Wrapper>
    </S.modal.Overlay>
  )
}

/** @deprecated use the `ModalWithButtons` component instead */
export const DeprecatedModal = styled(DeprecatedModalComponent)<DeprecatedModalProps>``
DeprecatedModal.displayName = 'DeprecatedModal'

const SmallWrapper = transition.fromTop(S.modal.SmallWrapper)

type SmallModalProps = DeprecatedModalProps & {
  title: React.ReactNode
}

export function SmallModalComponent({
  children,
  className,
  closable,
  onClose,
  open,
  title,
}: SmallModalProps): React.ReactElement<SmallModalProps> | null {
  const { step, unmounted, onTransitionEnd } = useTransition(open)

  if (unmounted) {
    return null
  }

  return (
    <SmallWrapper className={className} step={step} onTransitionEnd={onTransitionEnd}>
      <S.modal.Title>{title}</S.modal.Title>
      {closable && <glyphs.stroke.Close size={14} onClick={onClose} />}
      <S.modal.Body>{children}</S.modal.Body>
    </SmallWrapper>
  )
}

export const SmallModal = styled(SmallModalComponent)<SmallModalProps>``
SmallModal.displayName = 'SmallModal'

export function OverlayModalComponent({
  children,
  className,
  closable,
  onClose,
  open,
}: DeprecatedModalProps): React.ReactElement<DeprecatedModalProps> | null {
  if (!open) {
    return null
  }

  return (
    <S.modal.Overlay className={className}>
      <DisableBodyScroll />
      <S.modal.Wrapper>
        {closable && <glyphs.stroke.Close size={14} onClick={onClose} />}
        <S.modal.Body>{children}</S.modal.Body>
      </S.modal.Wrapper>
    </S.modal.Overlay>
  )
}

const DisableBodyScroll = createGlobalStyle`
  body {
    overflow: hidden;
  }
`

export const OverlayModal = styled(OverlayModalComponent)<DeprecatedModalProps>`
  ${S.modal.Wrapper} {
    max-width: none;
    position: absolute;
    overflow: auto;
    max-height: initial;

    &::before {
      content: none;
    }

    ${glyphs.stroke.Close} {
      left: auto;
      top: 20px;
      right: 20px;
      color: ${colors.GREY_SHADE_4};
      width: 22px;
      height: 22px;
    }
  }
`
SmallModal.displayName = 'OverlayModal'
